import classNames from 'classnames';
import React, { useCallback } from 'react';
import { useOutliersConfigSlice } from 'widgets/outliers/store/config/configSlice';
import WidgetSettingsNumericValueEditor from 'widgets/WidgetSettingsEditing/WidgetSettingsNumericValueEditor';
import { OutliersBenchmarkTypeDropdown } from 'widgets/outliers/components/OutliersBenchmarkTypeDropdown';
import {
    OutliersBenchmarkType,
    OutliersPlType,
} from 'widgets/outliers/store/config/OutliersConfigState';
import './Outliers.scss';
import { OutliersPlTypeDropdown } from 'widgets/outliers/components/OutliersPLTypeDropdown';
import WidgetSettingsCheckboxValueEditor from 'widgets/WidgetSettingsEditing/WidgetSettingsCheckboxValueEditor';
import { DockPosition } from '@instinet-analytics/semantic-ui-components';
import { DockPositionEditor } from '@instinet-analytics/semantic-ui-components';
import { useSelectIsFeatureEnabled } from 'app/store/featureToggles/selectors';
import { WellKnownFeatureToggleKeys } from 'app/store/featureToggles/FeatureTogglesState';
import { RadioGroup } from '@instinet-analytics/semantic-ui-components';
import './OutliersSettings.scss';

export function OutliersSettings() {
    const { config, dispatchConfigChanged } = useOutliersConfigSlice();
    const { flipAxis } = config;

    const isLastPriceEnabled = useSelectIsFeatureEnabled(
        WellKnownFeatureToggleKeys.LastPriceEnabled
    );

    const onBenchmarkChanged = (nextBenchmark?: OutliersBenchmarkType) => {
        dispatchConfigChanged({ benchmarkType: nextBenchmark });
    };

    const onPlChanged = (nextPl?: OutliersPlType) => {
        dispatchConfigChanged({ plType: nextPl });
    };

    const onDockPositionChanged = (nextPos?: DockPosition) => {
        dispatchConfigChanged({ dockPosition: nextPos });
    };

    type FlipAxisKeyValue = 'true' | 'false';
    const axisOptions: { key: FlipAxisKeyValue; label: string }[] = [
        { key: 'false', label: 'x: P&L, y: Benchmark Performance' },
        { key: 'true', label: 'x: Benchmark Performance, y: P&L' },
    ];

    const onFlipAxisChange = useCallback(
        (nextFlipAxis: FlipAxisKeyValue) => {
            dispatchConfigChanged({ flipAxis: nextFlipAxis === 'true' });
        },
        [dispatchConfigChanged]
    );

    return (
        <div className={classNames('OutliersSettings')}>
            <div className="group">
                {isLastPriceEnabled && (
                    <div>
                        <div>P&L</div>
                        <OutliersPlTypeDropdown
                            onChange={onPlChanged}
                            value={config.plType}
                        />
                    </div>
                )}
                <div>
                    <div>Benchmarked to</div>
                    <OutliersBenchmarkTypeDropdown
                        onChange={onBenchmarkChanged}
                        value={config.benchmarkType}
                    />
                </div>
            </div>
            <div className="group">
                <div>Axis:</div>
                <RadioGroup<FlipAxisKeyValue>
                    selected={flipAxis ? 'true' : 'false'}
                    onChange={onFlipAxisChange}
                    options={axisOptions}
                />
            </div>
            <div className="group">
                <WidgetSettingsCheckboxValueEditor
                    propertyName={'showGrid'}
                    config={config}
                    label="Show Grid?"
                    onChange={dispatchConfigChanged}
                />
                <div>
                    <div>Position:</div>
                    <DockPositionEditor
                        position={config.dockPosition ?? 'Bottom'}
                        onChange={onDockPositionChanged}
                    />
                </div>
            </div>
            <div className="group">
                <WidgetSettingsNumericValueEditor
                    className="horiz-numeric decimals"
                    propertyName={'avgPriceDecimals'}
                    label="Average Price decimal places"
                    config={config}
                    onChange={dispatchConfigChanged}
                    max={4}
                    min={0}
                />

                <WidgetSettingsNumericValueEditor
                    className="horiz-numeric numOrOrders"
                    label="Number of Orders"
                    propertyName={'numberOfOrders'}
                    config={config}
                    onChange={dispatchConfigChanged}
                    min={5}
                    max={100}
                />
            </div>
            <div className="group">
                <WidgetSettingsCheckboxValueEditor
                    propertyName={'hideTitlePanel'}
                    config={config}
                    label="Hide title panel?"
                    onChange={dispatchConfigChanged}
                />
            </div>
        </div>
    );
}
